import React, { useState, useEffect, useRef } from "react";
import { 
  Search, Paperclip, 
  Send, RefreshCw, ChevronDown, Check, X
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { base44 } from "@/api/base44Client";

export default function OutlookTaskPane() {
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isComposeMode, setIsComposeMode] = useState(false);

  // Email context states
  const [senderEmail, setSenderEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [attachments, setAttachments] = useState([]); // [{ filename, contentType, base64 }]

  // Detected Client metadata
  const [detectedClient, setDetectedClient] = useState(null); // { id, name }
  const [taskTypes, setTaskTypes] = useState([]);

  // Form selections
  const [selectedMatter, setSelectedMatter] = useState(null); // { id, name }
  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [reference, setReference] = useState("");
  const [comments, setComments] = useState("");
  const [includeEmail, setIncludeEmail] = useState(true);
  const [includeAttachments, setIncludeAttachments] = useState(true);

  // Matter search states
  const [matterSearchQuery, setMatterSearchQuery] = useState("");
  const [matterSuggestions, setMatterSuggestions] = useState([]);
  const [isSearchingMatters, setIsSearchingMatters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // 1. Initialize Office.js or load mock data
  useEffect(() => {
    if (window.Office) {
      window.Office.onReady((info) => {
        if (info.host === window.Office.HostType.Outlook) {
          setIsOfficeReady(true);
          const item = window.Office.context.mailbox.item;
          const isCompose = !!(item && item.subject && typeof item.subject.setAsync === "function");
          setIsComposeMode(isCompose);
          extractEmailContext();
        } else {
          loadBrowserMockContext();
        }
      });
    } else {
      loadBrowserMockContext();
    }

    // Close suggestions dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 2. Client Auto Detection: Fetch task types when sender email is resolved
  useEffect(() => {
    if (senderEmail) {
      detectClientAndLoadTaskTypes();
    }
  }, [senderEmail]);

  // 3. Debounced Matter Autocomplete Search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (matterSearchQuery.trim().length >= 2) {
        searchMatters(matterSearchQuery);
      } else {
        setMatterSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [matterSearchQuery]);

  // Load browser fallback mock data for local testing
  const loadBrowserMockContext = () => {
    console.log("Outlook Add-in loading in browser mock mode.");
    setSenderEmail("john@lacw.org.au"); // Trigger client detection for LACW
    setSubject("Guideline certification for Smith v Jones case");
    setEmailBody("Dear Complete Law Support,\n\nPlease see attached the Guideline compliance report for Smith v Jones.\n\nWarm regards,\nJohn");
    setAttachments([
      { filename: "Guideline_Report_Smith.pdf", contentType: "application/pdf", base64: "JVBERi0xLjQKJVRlc3QgUERGIHBheWxvYWQgLSBDTFMgQ29ubmVjdCBBZGQtaW4=" }
    ]);
  };

  // Extract real Outlook email metadata using Office.js
  const extractEmailContext = () => {
    const item = window.Office.context.mailbox.item;
    if (!item) return;

    // A. Sender Email Address
    if (item.from) {
      setSenderEmail(item.from.emailAddress || "");
    } else if (window.Office.context.mailbox.userProfile) {
      setSenderEmail(window.Office.context.mailbox.userProfile.emailAddress || "");
    }

    // B. Subject
    setSubject(item.subject || "No Subject");

    // C. Get Email Body Text
    if (item.body) {
      item.body.getAsync(window.Office.CoercionType.Text, (result) => {
        if (result.status === window.Office.AsyncResultStatus.Succeeded) {
          setEmailBody(result.value || "");
        }
      });
    }

    // D. Ingest Attachments List
    if (item.attachments && item.attachments.length > 0) {
      const attPromises = item.attachments.map(att => {
        return new Promise((resolve) => {
          // Fetch attachment base64 payload
          if (item.getAttachmentContentAsync) {
            item.getAttachmentContentAsync(att.id, (result) => {
              if (result.status === window.Office.AsyncResultStatus.Succeeded) {
                resolve({
                  filename: att.name,
                  contentType: att.contentType || "application/octet-stream",
                  base64: result.value.content // Base64 payload is contained in .content
                });
              } else {
                resolve(null);
              }
            });
          } else {
            // Fallback for older Exchange client versions where getAttachmentContentAsync is not present
            resolve({
              filename: att.name,
              contentType: att.contentType || "application/octet-stream",
              base64: "" // Ingest file entry but skip payload
            });
          }
        });
      });

      Promise.all(attPromises).then(results => {
        const validAtts = results.filter(a => a !== null);
        setAttachments(validAtts);
      });
    }
  };

  const handleCategoryClick = (categoryName) => {
    setIsLoading(true);
    if (window.Office && window.Office.context && window.Office.context.mailbox && window.Office.context.mailbox.item) {
      const item = window.Office.context.mailbox.item;
      item.subject.getAsync((result) => {
        if (result.status === window.Office.AsyncResultStatus.Failed) {
          console.error("Failed to retrieve subject:", result.error.message);
          setIsLoading(false);
          return;
        }

        const currentSubject = result.value || "";
        const tagRegex = /^\[(New aid request|Extension request|Disbursment request|Process payment - fee slip|Process payment - invoice|In house billing|Travel Claim|ACF|Follow up)\]\s*/i;
        const cleanedSubject = currentSubject.replace(tagRegex, "");
        const newSubject = `[${categoryName}] ${cleanedSubject}`.trim();

        item.subject.setAsync(newSubject, (setResult) => {
          setIsLoading(false);
          if (setResult.status === window.Office.AsyncResultStatus.Failed) {
            console.error("Failed to set subject:", setResult.error.message);
            toast.error("Failed to apply category to email subject.");
          } else {
            // Close the taskpane container immediately
            window.Office.context.ui.closeContainer();
          }
        });
      });
    } else {
      // Mock environment
      setTimeout(() => {
        setIsLoading(false);
        toast.success(`Subject tagged with: [${categoryName}] (Mock Mode)`);
      }, 500);
    }
  };

  // Call GET /api/task-types for Client Detection & Dropdown loading
  const detectClientAndLoadTaskTypes = async () => {
    try {
      // Authenticate call (uses session token if user is signed into portal in browser)
      // For local add-in context, we use the autodetect URL
      const data = await base44.taskTypes.get(senderEmail);
      setDetectedClient(data.client);
      setTaskTypes(data.taskTypes);
      
      // Auto select first task type if available
      if (data.taskTypes && data.taskTypes.length > 0) {
        setSelectedTaskTypeId(data.taskTypes[0].id);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to detect client details");
    }
  };

  // Call GET /api/matters?q=... for live typeahead matter searches
  const searchMatters = async (query) => {
    setIsSearchingMatters(true);
    try {
      const list = await base44.matters.search(query);
      setMatterSuggestions(list);
      setShowSuggestions(true);
    } catch (e) {
      console.error("Fuzzy matter search failed:", e);
    } finally {
      setIsSearchingMatters(false);
    }
  };

  // Submit Submission Ingestion
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!selectedMatter) {
      toast.error("Please search and select a matter.");
      return;
    }
    if (!selectedTaskTypeId) {
      toast.error("Please select a task type.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        clientId: detectedClient?.id || "lacw",
        matterId: selectedMatter.id,
        taskTypeId: selectedTaskTypeId,
        priority,
        subject,
        body: includeEmail ? emailBody : "",
        comments: comments || reference,
        attachments: includeAttachments ? attachments : [],
        senderEmail
      };

      await base44.submissions.create(payload);

      // Inject beautifully styled request metadata card directly into the email message body
      if (window.Office && window.Office.context && window.Office.context.mailbox && window.Office.context.mailbox.item && window.Office.context.mailbox.item.body) {
        const item = window.Office.context.mailbox.item;
        const taskTypeName = taskTypes.find(t => t.id === selectedTaskTypeId)?.name || "General Request";
        const emailCard = `
          <div style="font-family: 'Segoe UI', Arial, sans-serif; border: 1px solid #d1d5db; border-left: 4px solid #9333ea; border-radius: 6px; padding: 12px; margin: 12px 0; background-color: #f9fafb; max-width: 480px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
            <div style="font-size: 13px; font-weight: bold; color: #1f2937; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span style="background-color: #9333ea; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 800;">CLS INGESTION</span>
              <span>Request Successfully Registered</span>
            </div>
            <table style="width: 100%; font-size: 12px; color: #4b5563; border-collapse: collapse;">
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 6px 0; font-weight: bold;">Client Account:</td><td style="padding: 6px 0; text-align: right; font-weight: 600;">${detectedClient?.name || "LACW"}</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 6px 0; font-weight: bold;">Matter Case:</td><td style="padding: 6px 0; text-align: right; font-weight: 600;">${selectedMatter.name}</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 6px 0; font-weight: bold;">Request Type:</td><td style="padding: 6px 0; text-align: right; color: #9333ea; font-weight: bold;">${taskTypeName}</td></tr>
              <tr><td style="padding: 6px 0; font-weight: bold;">Escalation Priority:</td><td style="padding: 6px 0; text-align: right; color: ${priority === 'Urgent' ? '#ef4444' : priority === 'High' ? '#f97316' : '#10b981'}; font-weight: bold;">${priority}</td></tr>
            </table>
          </div>
          <br/>
        `;
        
        // Write HTML block at cursor position
        item.body.setSelectedDataAsync(emailCard, { coercionType: window.Office.CoercionType.Html }, (result) => {
          if (result.status === window.Office.AsyncResultStatus.Failed) {
            console.error("Failed to inject metadata card to email body:", result.error.message);
          }
        });
      }

      setIsSubmitted(true);
      toast.success("Submission successfully ingested into complete law support gateway!");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to submit request.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 flex flex-col font-sans max-w-[400px] border-r border-slate-200 dark:border-slate-800 shadow-lg mx-auto">
      
      {/* Title Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-purple-650 flex items-center justify-center text-white font-extrabold text-[10px]">
            C
          </div>
          <span className="text-sm font-extrabold tracking-tight">Complete Law Support</span>
        </div>
        {!isOfficeReady && (
          <span className="text-[9px] bg-amber-100 text-amber-850 dark:bg-amber-950/40 dark:text-amber-300 px-2 py-0.5 rounded-full font-bold border border-amber-200/40">
            Mock Environment
          </span>
        )}
      </header>

      {/* Main Form content */}
      <div className="flex-1 p-4 overflow-y-auto">
        {isComposeMode ? (
          <div className="space-y-4 animate-fade-in text-center">
            <div className="mb-2 text-left px-1">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Select Category</h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 leading-relaxed">
                Click a category below to tag this email draft. The sidebar will close automatically.
              </p>
            </div>
            
            {isLoading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />
                <span className="text-xs text-slate-400 font-bold">Applying category...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-w-[320px] mx-auto pt-1">
                {[
                  "New aid request",
                  "Extension request",
                  "Disbursment request",
                  "Process payment - fee slip",
                  "Process payment - invoice",
                  "In house billing",
                  "Travel Claim",
                  "ACF",
                  "Follow up"
                ].map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryClick(category)}
                    className="w-full bg-white dark:bg-slate-850 hover:border-purple-600 hover:text-purple-600 dark:hover:text-purple-400 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3.5 text-[11px] font-bold text-left text-slate-700 dark:text-slate-200 transition-all hover:scale-[1.01] hover:shadow-md cursor-pointer"
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : isSubmitted ? (
          <div className="text-center py-10 space-y-4 animate-fade-in">
            <div className="w-12 h-12 rounded-full bg-green-150 dark:bg-green-950/20 text-green-600 dark:text-green-400 flex items-center justify-center mx-auto shadow-sm">
              <Check className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-sm font-bold">Request Submitted!</h3>
              <p className="text-xs text-slate-400 dark:text-slate-550 mt-1 max-w-[200px] mx-auto">
                Your request has been ingested. The backend portal is running automated workflow steps.
              </p>
            </div>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setSelectedMatter(null);
                setMatterSearchQuery("");
                setComments("");
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition-all shadow-sm"
            >
              Submit Another Request
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitRequest} className="space-y-4 text-xs font-semibold">
            
            {/* 1. Client Detection Info (Read Only) */}
            <div className="space-y-1">
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Detected Client
              </span>
              <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 flex items-center justify-between">
                <span className="font-bold text-slate-800 dark:text-slate-150">
                  {detectedClient ? detectedClient.name : "Detecting client..."}
                </span>
                <span className="text-[10px] text-slate-400 font-medium">({senderEmail || "No Email Resolved"})</span>
              </div>
            </div>

            {/* 2. Matter Search typeahead */}
            <div className="space-y-1.5 relative" ref={suggestionsRef}>
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Matter Case
              </span>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Type to search matters (e.g. Smith)..."
                  value={selectedMatter ? selectedMatter.name : matterSearchQuery}
                  onChange={(e) => {
                    if (selectedMatter) {
                      setSelectedMatter(null);
                      setMatterSearchQuery("");
                    } else {
                      setMatterSearchQuery(e.target.value);
                    }
                  }}
                  className="w-full pl-8 pr-8 py-2 border border-slate-250 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-850 text-slate-800 dark:text-slate-100 shadow-sm"
                />
                {(selectedMatter || matterSearchQuery) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMatter(null);
                      setMatterSearchQuery("");
                      setMatterSuggestions([]);
                    }}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Matter Suggestions dropdown list */}
              {showSuggestions && matterSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg rounded-xl z-25 max-h-40 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/50">
                  {matterSuggestions.map(m => (
                    <div
                      key={m.id}
                      onClick={() => {
                        setSelectedMatter(m);
                        setShowSuggestions(false);
                      }}
                      className="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-750 cursor-pointer text-left text-[11px] font-bold text-slate-755 dark:text-slate-200"
                    >
                      {m.name} <span className="text-[9px] text-slate-400 font-medium">({m.id})</span>
                    </div>
                  ))}
                </div>
              )}

              {isSearchingMatters && (
                <div className="absolute right-9 top-2.5">
                  <div className="w-3.5 h-3.5 border border-slate-200 border-t-purple-600 rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* 3. Task Type Dropdown */}
            <div className="space-y-1.5">
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Task Request Type
              </span>
              <div className="relative">
                <select
                  value={selectedTaskTypeId}
                  onChange={(e) => setSelectedTaskTypeId(e.target.value)}
                  className="w-full bg-white dark:bg-slate-850 border border-slate-250 dark:border-slate-700 rounded-xl px-3 py-2.5 font-bold text-slate-800 dark:text-slate-100 appearance-none cursor-pointer shadow-sm focus:ring-1 focus:ring-purple-600"
                >
                  <option value="" disabled>Select Task Type...</option>
                  {taskTypes.map(tt => (
                    <option key={tt.id} value={tt.id}>{tt.name}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            {/* 4. Priority Rules Radios */}
            <div className="space-y-1.5">
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Escalation Priority
              </span>
              <div className="flex gap-4 pt-1">
                {["Normal", "High", "Urgent"].map(p => (
                  <label key={p} className="flex items-center gap-1.5 cursor-pointer font-bold">
                    <input
                      type="radio"
                      name="priority"
                      value={p}
                      checked={priority === p}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-3.5 h-3.5 text-purple-650 focus:ring-purple-500 border-slate-300"
                    />
                    <span className={cn(
                      p === "Urgent" && "text-red-500",
                      p === "High" && "text-orange-500"
                    )}>
                      {p}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* 5. Reference Input */}
            <div className="space-y-1.5">
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Case Reference
              </span>
              <input
                type="text"
                placeholder="Optional case file / billing ref..."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full px-3 py-2 border border-slate-250 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-850 shadow-sm"
              />
            </div>

            {/* 6. Comments TextArea */}
            <div className="space-y-1.5">
              <span className="text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                Comments
              </span>
              <textarea
                placeholder="Add special instructions or comments for operator triage..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-slate-250 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-850 shadow-sm font-sans"
              />
            </div>

            {/* 7. Include Email and Attachments Checkboxes */}
            <div className="space-y-2 border-t border-slate-200 dark:border-slate-750 pt-3">
              <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={includeEmail}
                  onChange={(e) => setIncludeEmail(e.target.checked)}
                  className="rounded text-purple-650 focus:ring-purple-500 w-3.5 h-3.5"
                />
                <span>Include Email Body Text ({emailBody ? `${emailBody.length} chars` : "0"})</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={includeAttachments}
                  onChange={(e) => setIncludeAttachments(e.target.checked)}
                  className="rounded text-purple-650 focus:ring-purple-500 w-3.5 h-3.5"
                />
                <span className="flex items-center gap-1">
                  Include Attachments ({attachments.length})
                </span>
              </label>

              {includeAttachments && attachments.length > 0 && (
                <div className="pl-5 space-y-1">
                  {attachments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-[10px] text-slate-400 truncate">
                      <Paperclip className="w-3 h-3 text-slate-400" />
                      <span>{a.filename}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || !selectedMatter || !selectedTaskTypeId}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 dark:disabled:bg-purple-900 text-white font-extrabold py-2.5 rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 mt-2"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Submit Request
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
